import { useEffect, useState, useCallback, useMemo } from "react";

import Spinner from "../../../shared/ui/Loading/Spinner";
import EmptyState from "../../../shared/ui/EmptyState/EmptyState";
import Text from "../../../shared/ui/Typography/Text";
import InlineError from "../../../shared/ui/Field/InlineError";

import CompanyContactField from "../ui/CompanyContactField";
import {
  fetchCompany,
  patchCompanyContact,
} from "../api/companyContact";
import { parseDrfFieldErrors, messageFromError } from "../api/errors";

// CompanyContactForm — feature widget. Owns local state for the
// text-input form lifecycle: load, edit, validate, submit, sync.
// No Redux slice — company-contact data is not consumed cross-feature
// (verified by grep, see migration/phase9-text-form-plan.md §5).

// The editable field set. Order drives render order. Anything outside
// this whitelist is preserved from `baseline` and never sent in PATCH
// bodies. See migration/phase9-text-form-plan.md §1 for the choice of
// fields and §1.1 for the deferrals.
const EDITABLE_FIELDS = [
  {
    name: "email",
    type: "email",
    label: "Електронна пошта",
    help: "Загальна адреса для контакту з компанією.",
    placeholder: "company@example.com",
    autoComplete: "email",
  },
  {
    name: "phone",
    type: "tel",
    label: "Телефон",
    help: "Контактний телефон компанії.",
    placeholder: "+380 …",
    maxLength: 32,
    autoComplete: "tel",
  },
  {
    name: "website",
    type: "url",
    label: "Вебсайт",
    help: "Повна URL-адреса, починаючи з https://.",
    placeholder: "https://…",
    autoComplete: "url",
  },
];

const EDITABLE_KEYS = EDITABLE_FIELDS.map((f) => f.name);
const EMPTY_VALUES = EDITABLE_KEYS.reduce((acc, key) => {
  acc[key] = "";
  return acc;
}, {});

const extractEditable = (company) => {
  if (!company) return { ...EMPTY_VALUES };
  const next = {};
  for (const key of EDITABLE_KEYS) {
    next[key] = typeof company[key] === "string" ? company[key] : "";
  }
  return next;
};

const buildPatchBody = (values, baseline) => {
  const diff = {};
  for (const key of EDITABLE_KEYS) {
    if ((values[key] ?? "") !== (baseline[key] ?? "")) {
      diff[key] = values[key] ?? "";
    }
  }
  return diff;
};

const shallowEqualEditable = (a, b) => {
  if (!a || !b) return false;
  for (const key of EDITABLE_KEYS) {
    if ((a[key] ?? "") !== (b[key] ?? "")) return false;
  }
  return true;
};

// Client-side hints. Deliberately liberal — the server is the
// authoritative validator. See plan §7.1.
const looksLikeEmail = (value) => /.+@.+\..+/.test(value);
const looksLikeUrl = (value) => /^https?:\/\/.+/i.test(value);

const validateClient = (values) => {
  const errors = {};
  const email = (values.email ?? "").trim();
  const phone = values.phone ?? "";
  const website = (values.website ?? "").trim();

  if (email && !looksLikeEmail(email)) {
    errors.email = "Введіть коректну електронну адресу.";
  }
  if (phone.length > 32) {
    errors.phone = "Не довше 32 символів.";
  }
  if (website && !looksLikeUrl(website)) {
    errors.website = "Введіть повну URL-адресу (з https://).";
  }
  return errors;
};

const omitKey = (obj, key) => {
  if (!obj || obj[key] === undefined) return obj || {};
  const next = { ...obj };
  delete next[key];
  return next;
};

function CompanyContactForm({
  companyId,
  onDirtyChange,
  onBusyChange,
  writesSuspended = false,
}) {
  const [baseline, setBaseline] = useState(null);
  const [values, setValues] = useState(null);
  const [loadState, setLoadState] = useState("loading");
  const [loadError, setLoadError] = useState(null);
  const [saveState, setSaveState] = useState("idle");
  const [fieldErrors, setFieldErrors] = useState({});
  const [clientErrors, setClientErrors] = useState({});

  useEffect(() => {
    if (!companyId) {
      setLoadState("not-found");
      return undefined;
    }

    let cancelled = false;
    setLoadState("loading");
    setLoadError(null);

    fetchCompany(companyId)
      .then((data) => {
        if (cancelled) return;
        setBaseline(data);
        setValues(extractEditable(data));
        setLoadState("loaded");
      })
      .catch((err) => {
        if (cancelled) return;
        const status = err && err.response && err.response.status;
        if (status === 403) {
          setLoadState("forbidden");
        } else if (status === 404) {
          setLoadState("not-found");
        } else {
          setLoadError(messageFromError(err));
          setLoadState("error");
        }
      });

    return () => {
      cancelled = true;
    };
  }, [companyId]);

  const handleValueChange = useCallback((name, value) => {
    setValues((prev) => (prev ? { ...prev, [name]: value } : prev));
    setFieldErrors((prev) => omitKey(prev, name));
    setClientErrors((prev) => omitKey(prev, name));
    setSaveState((prev) => (prev === "saved" ? "idle" : prev));
  }, []);

  const dirty = useMemo(() => {
    if (!baseline || !values) return false;
    const baselineEditable = extractEditable(baseline);
    return !shallowEqualEditable(baselineEditable, values);
  }, [baseline, values]);

  useEffect(() => {
    if (typeof onDirtyChange === "function") onDirtyChange(dirty);
  }, [dirty, onDirtyChange]);

  useEffect(() => {
    if (typeof onBusyChange === "function") {
      onBusyChange(saveState === "saving");
    }
  }, [onBusyChange, saveState]);

  const handleSubmit = async (event) => {
    if (event) event.preventDefault();
    if (writesSuspended) return;
    if (!values || !baseline || !dirty || saveState === "saving") return;

    const clientErrs = validateClient(values);
    if (Object.keys(clientErrs).length > 0) {
      setClientErrors(clientErrs);
      setFieldErrors({});
      return;
    }

    setClientErrors({});
    setFieldErrors({});
    setSaveState("saving");

    try {
      const baselineEditable = extractEditable(baseline);
      const body = buildPatchBody(values, baselineEditable);
      const updated = await patchCompanyContact(companyId, body);
      setBaseline(updated);
      setValues(extractEditable(updated));
      setSaveState("saved");
    } catch (err) {
      setFieldErrors(parseDrfFieldErrors(err, EDITABLE_KEYS));
      setSaveState("error");
    }
  };

  const handleReset = () => {
    if (!baseline || saveState === "saving" || writesSuspended) return;
    setValues(extractEditable(baseline));
    setClientErrors({});
    setFieldErrors({});
    setSaveState((prev) => (prev === "saved" ? "idle" : prev));
  };

  if (loadState === "loading") {
    return (
      <div className="feature-company-contact__state">
        <Spinner size="lg" label="Завантаження даних компанії" />
      </div>
    );
  }

  if (loadState === "forbidden") {
    return (
      <div className="feature-company-contact__state">
        <EmptyState
          icon="fa-solid fa-lock"
          title="Немає доступу"
          description="Редагувати контактні дані може лише адміністратор або власник компанії."
          size="md"
        />
      </div>
    );
  }

  if (loadState === "not-found") {
    return (
      <div className="feature-company-contact__state">
        <EmptyState
          icon="fa-solid fa-magnifying-glass"
          title="Компанію не знайдено"
          description="Перевірте адресу сторінки або поверніться до списку компаній."
          size="md"
        />
      </div>
    );
  }

  if (loadState === "error") {
    return (
      <div className="feature-company-contact__state">
        <EmptyState
          icon="fa-solid fa-triangle-exclamation"
          title="Не вдалося завантажити компанію"
          description={loadError || "Спробуйте оновити сторінку."}
          size="md"
        />
      </div>
    );
  }

  if (!values) return null;

  const saving = saveState === "saving";
  const fieldsDisabled = saving || writesSuspended;
  const formError = fieldErrors._form;

  return (
    <form
      className="feature-company-contact__form"
      data-feature="company-contact-form"
      onSubmit={handleSubmit}
      noValidate
    >
      <div className="feature-company-contact__rows">
        {EDITABLE_FIELDS.map((field) => (
          <CompanyContactField
            key={field.name}
            name={field.name}
            label={field.label}
            help={field.help}
            type={field.type}
            value={values[field.name]}
            error={clientErrors[field.name] || fieldErrors[field.name]}
            placeholder={field.placeholder}
            maxLength={field.maxLength}
            autoComplete={field.autoComplete}
            disabled={fieldsDisabled}
            onValueChange={handleValueChange}
          />
        ))}
      </div>

      <div className="feature-company-contact__messages">
        {formError ? (
          <InlineError className="feature-company-contact__message">
            {formError}
          </InlineError>
        ) : null}
        {saveState === "saved" && !dirty ? (
          <Text
            as="span"
            size="xs"
            tone="success"
            className="feature-company-contact__saved"
          >
            Контактні дані збережено.
          </Text>
        ) : null}
      </div>

      <div className="feature-company-contact__actions">
        <button
          type="button"
          className="feature-company-contact__reset-button"
          onClick={handleReset}
          disabled={!dirty || fieldsDisabled}
        >
          Скасувати зміни
        </button>
        <button
          type="submit"
          className="feature-company-contact__save-button"
          disabled={!dirty || fieldsDisabled}
          data-loading={saving ? "true" : undefined}
        >
          {saving ? (
            <Spinner size="sm" label="Збереження" />
          ) : (
            "Зберегти зміни"
          )}
        </button>
      </div>
    </form>
  );
}

export default CompanyContactForm;
